<template>
  <div v-if="loading"><spinner /></div>
  <div v-else class="container">
    <h1 id="page-title">{{ adminText('organizationAll.tags_848eed0f', 'Tags') }}</h1>
    <p class="page-description">{{ adminText('organizationAll.add_and_manage_your_tags_689b9962', 'Add and manage your tags.') }}</p>
    <main-button
      style="max-width: 256px; margin-bottom: 1rem"
      @click="openCreateModal = !openCreateModal"
    >
      <span class="add-symbol">+</span>{{ adminText('organizationAll.create_new_tag_1f2dc9a7', 'Create new tag') }}
    </main-button>
    <div v-if="!$apollo.loading" class="attendee_table">
      <div
        v-if="interests.length === 0"
        class="no-attendees-message-container border__left border__right"
      >
        <span class="no-attendees-message">{{ adminText('organizationAll.no_matching_records_found_0c7f0a64', 'No matching records found.') }}</span>
      </div>
      <table-list
        :hide-checkbox="true"
        :selected.sync="selected"
        :organization-slug="organizationSlug"
        :attendees="interests"
        body-style="grid-auto-columns: 50px minmax(150px, 250px) !important;"
        :visible-fields="visibleFields"
        :attendee-fields="{}"
        :pagination="pagination"
        @click="openModal($event)"
        @sort="handleSort($event)"
      />
      <table-pagination
        :pagination="pagination"
        :total-attendees="totalInterestsCount"
        @change="handlePagination($event)"
        @changePerPage="handlePagination($event)"
      />
    </div>
    <create-tag-modal
      v-if="openCreateModal"
      @create="handleCreateTag"
      @close="openCreateModal = !openCreateModal"
      @closeModal="openCreateModal = !openCreateModal"
    />
    <edit-tag-modal
      v-if="openEditModal"
      :initial-values="selectedInterest"
      :create-action="false"
      @delete="handleDeleteTag"
      @update="handleUpdateTag"
      @closeModal="openEditModal = !openEditModal"
      @close="openEditModal = !openEditModal"
    />
    <simple-snackbar
      :open.sync="snackbar.open"
      :message-prop="organizationText(snackbar.message)"
      :color="snackbar.color"
    />
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import {
  GET_ORGANIZATION_INTEREST,
  CREATE_INTEREST,
  DELETE_INTEREST,
  UPDATE_INTEREST,
} from '@/graphql/interest.graphql'
import tableList from '../../accreditations/attendee-table/list.vue'
import editTagModal from '@/components/organizations/genres-and-tags/edit-tag-modal.vue'
import createTagModal from './create-tag-modal.vue'
import Spinner from '../../shared/spinner.vue'
import { InterestType } from '@/types'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import tablePagination from '../../accreditations/attendee-table/pagination.vue'

export default {
  mixins: [OrganizationLocalization],
  name: 'tags',
  components: {
    tableList,
    SimpleSnackbar,
    tablePagination,
    editTagModal,
    createTagModal,
    Spinner,
  },
  props: {
    organizationSlug: {
      type: String,
    },
  },
  data() {
    const { pagination } = this.getInitialFromQueryParams()

    return {
      interests: [],
      pagination,
      InterestType,
      selectedInterest: {},
      selected: [],
      one: 1,
      loading: true,
      snackbar: {
        open: false,
        message: '',
        color: 'primary',
      },
      openCreateModal: false,
      openEditModal: false,
      visibleFields: ['Name'],
      totalInterestsCount: 0,
    }
  },
  async mounted() {
    await this.fetchInterests()
  },
  methods: {
    openModal(id) {
      this.selectedInterest = {
        ...this.interests.find(interest => interest.id === id),
      }
      this.openEditModal = !this.openEditModal
    },
    async handleCreateTag(tagName) {
      const interestInput = { name: tagName, categoryId: 5, type: InterestType.TAG }
      const { errors } = await this.$apollo.mutate({
        mutation: CREATE_INTEREST,
        variables: { organizationSlug: this.organizationSlug, interestInput },
      })
      await this.showUserMessage(errors, 'created')
    },
    async handleRefetchData() {
      this.selected = []
      this.openEditModal = false
      this.openCreateModal = false
      await this.fetchInterests()
    },
    async showUserMessage(errors, msg) {
      if (errors) {
        this.snackbar = {
          open: true,
          message: 'Oops, something went wrong!',
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          message: 'Tag created successfully!',
        }
        await this.handleRefetchData()
      }
    },
    async handleDeleteTag(interestId) {
      const { errors } = await this.$apollo.mutate({
        mutation: DELETE_INTEREST,
        variables: {
          organizationSlug: this.organizationSlug,
          interestId,
        },
      })
      await this.showUserMessage(errors, 'deleted')
    },
    async handleUpdateTag(interestInput) {
      const { id, ...rest } = interestInput
      const { errors } = await this.$apollo.mutate({
        mutation: UPDATE_INTEREST,
        variables: {
          organizationSlug: this.organizationSlug,
          interestId: id,
          interestInput: rest,
        },
      })
      await this.showUserMessage(errors, 'updated')
    },
    async handlePagination({ page, per_page }) {
      const { page: currentPage, per_page: currentPerPage } = this.pagination
      if (currentPage > page && currentPage === this.one) return
      if (page > currentPage && !(this.totalInterestsCount - currentPerPage * currentPage > 0))
        return

      this.pagination = {
        ...this.pagination,
        per_page: per_page === 'All' ? -this.one : per_page,
        page: page,
      }

      await this.addPaginationToRoute(this.pagination)
    },
    async handleSort(sortBy) {
      const { per_page, page, sortBy: thisSortBy, descending } = this.pagination
      const pagination = {
        page,
        per_page,
        sortBy,
        descending: sortBy === thisSortBy ? !descending : false,
      }
      await this.addPaginationToRoute(pagination)
      this.pagination = pagination
    },
    getInitialFromQueryParams() {
      const ten = 10
      const { per_page, page, sortBy, descending } = this.$route.query

      return {
        pagination: {
          page: parseInt(page, ten) || this.one,
          per_page: parseInt(per_page, ten) || ten,
          sortBy: sortBy,
          descending: descending ? !!descending : false,
        },
      }
    },
    async addPaginationToRoute({ page, per_page, sortBy, descending }) {
      await this.$router.replace({
        query: {
          ...this.$route.query,
          per_page,
          page,
          sortBy,
          descending: descending ? this.one : 0,
        },
      })

      await this.fetchInterests()
    },
    async fetchInterests() {
      const {
        data: { getOrganizationInterest },
        errors,
      } = await this.$apollo.query({
        query: GET_ORGANIZATION_INTEREST,
        variables: {
          organizationSlug: this.organizationSlug,
        },
      })

      if (errors) {
        this.loading = false
        await this.showUserMessage(errors)
      } else {
        this.interests = getOrganizationInterest.filter(
          interest => interest.type === InterestType.TAG,
        )

        this.totalInterestsCount = this.interests.length
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.page-description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  color: #979797;
}
.container {
  padding: 0 !important;
  margin: 0 !important;
  display: flex;
  flex-direction: column;
  max-width: 43.75rem;
}
#page-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}
</style>
